import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useWholesaleCart } from '../../contexts/WholesaleCartContext';
import { useWholesaleAuth } from '../../contexts/WholesaleAuthContext';
import { db } from '../../services/firebase';
import { collection, addDoc } from 'firebase/firestore';
import { sendWholesaleOrderNotification } from '../../services/wholesaleNotificationService';
import { FiCreditCard, FiTruck, FiDollarSign, FiArrowLeft } from 'react-icons/fi';
import styles from './WholesaleCheckout.module.css';

const WholesaleCheckout = ({ customer }) => {
    const navigate = useNavigate();
    const { items: cartItems, clearCart, totalItems } = useWholesaleCart();
    const { currentWholesaleUser, wholesaleCustomer } = useWholesaleAuth();
    const [isProcessing, setIsProcessing] = useState(false);
    const [error, setError] = useState(null);
    const [success, setSuccess] = useState(false);

    const wholesaleCartItems = cartItems || [];
    const subtotal = wholesaleCartItems.reduce((acc, item) => acc + (item.wholesalePrice || 0) * item.quantity, 0);
    const shipping = 30.00;
    const total = subtotal + shipping;

    const validateOrder = () => {
        if (!wholesaleCustomer) {
            setError('Customer information not available.');
            return false;
        }
        
        if (wholesaleCustomer.status === 'demo') {
            setError('Demo accounts cannot place orders. Please contact us for full access.');
            return false;
        }
        
        if (wholesaleCartItems.length === 0) {
            setError('Your cart is empty.');
            return false;
        }
        
        setError(null);
        return true;
    };

    const handlePlaceOrder = async (paymentMethod) => {
        console.log(`Placing order with payment method: ${paymentMethod}`);
        console.log('Current wholesale user:', currentWholesaleUser);
        console.log('Wholesale customer:', wholesaleCustomer);
        console.log('Customer ID:', wholesaleCustomer?.id);
        console.log('Customer status:', wholesaleCustomer?.status);
        
        if (!validateOrder()) return;

        setIsProcessing(true);
        setError(null);
        
        const orderData = {
            customerId: wholesaleCustomer.id || '',
            customerName: wholesaleCustomer.businessName || '',
            contactPerson: wholesaleCustomer.contactName || 
                         `${wholesaleCustomer.contactFirstName || ''} ${wholesaleCustomer.contactLastName || ''}`.trim() || 'N/A',
            email: wholesaleCustomer.purchaseEmail || wholesaleCustomer.email || '',
            phone: wholesaleCustomer.purchasePhone || wholesaleCustomer.phone || '',
            address: wholesaleCustomer.streetAddress || wholesaleCustomer.address || 'On file',
            createdAt: new Date(),
            status: 'Processing',
            items: wholesaleCartItems,
            itemCount: totalItems || 0,
            subtotal: Number(subtotal) || 0,
            shipping: Number(shipping) || 0,
            total: Number(total) || 0,
            paymentMethod: paymentMethod || '',
            paymentStatus: 'pending',
        };
        
        try {
            const ordersRef = collection(db, 'wholesaleOrders');
            const docRef = await addDoc(ordersRef, orderData);
            console.log(`Order created successfully with ID: ${docRef.id}`);
            
            // Send WhatsApp notification
            try {
                console.log('Sending WhatsApp notification...');
                await sendWholesaleOrderNotification({
                    ...orderData,
                    orderId: docRef.id
                });
                console.log('WhatsApp notification sent successfully');
            } catch (notificationError) {
                console.error('WhatsApp notification failed:', notificationError);
                // Don't fail the order if notification fails
            }
            
            clearCart();
            setSuccess(true);
            
            // Show success for 3 seconds then redirect to orders section
            setTimeout(() => {
                navigate('/wholesale-dashboard/orders');
            }, 3000);
            
        } catch (err) {
            console.error('Order creation failed:', err);
            setError(`Failed to place order: ${err.message}`);
        } finally {
            setIsProcessing(false);
        }
    };

    if (success) {
        return (
            <div className={styles.container}>
                <div className={styles.successMessage}>
                    <div className={styles.successIcon}>✅</div>
                    <h2>Order Confirmed!</h2>
                    <p>Your wholesale order has been successfully placed.</p>
                    <p>We will contact you soon via WhatsApp for order confirmation and delivery details.</p>
                    <div className={styles.redirectMessage}>
                        Redirecting to your orders...
                    </div>
                </div>
            </div>
        );
    }

    if (wholesaleCartItems.length === 0) {
        return (
            <div className={styles.container}>
                <div className={styles.header}>
                    <button 
                        onClick={() => navigate('/wholesale-dashboard/catalog')} 
                        className={styles.backButton}
                    >
                        <FiArrowLeft /> Back to Catalog
                    </button>
                    <h1>Checkout</h1>
                </div>
                <div className={styles.emptyCart}>
                    <div className={styles.emptyIcon}>🛒</div>
                    <h3>Your cart is empty</h3>
                    <p>Add products to your cart to proceed with checkout</p>
                    <button 
                        className={styles.catalogButton}
                        onClick={() => navigate('/wholesale-dashboard/catalog')}
                    >
                        Browse Products
                    </button>
                </div>
            </div>
        );
    }

    return (
        <div className={styles.container}>
            <div className={styles.header}>
                <button 
                    onClick={() => navigate('/wholesale-dashboard/orders')} 
                    className={styles.backButton}
                >
                    <FiArrowLeft /> Back to Cart
                </button>
                <h1>Checkout</h1>
                <p>Complete your wholesale order</p>
            </div>

            <div className={styles.checkoutContent}>
                {/* Order Summary */}
                <div className={styles.orderSummary}>
                    <h3>Order Summary</h3>
                    <div className={styles.orderItems}>
                        {wholesaleCartItems.map(item => (
                            <div key={item.id} className={styles.orderItem}>
                                <img 
                                    src={item.imageUrl || 'https://via.placeholder.com/60'} 
                                    alt={item.name}
                                    className={styles.itemImage}
                                />
                                <div className={styles.itemDetails}>
                                    <div className={styles.itemName}>{item.name}</div>
                                    {(item.selectedWeight || item.selectedBeanType) && (
                                        <div className={styles.itemWeight}>
                                            {[
                                                item.selectedWeight?.label || item.selectedWeight || item.weightDisplay, 
                                                item.selectedBeanType || item.typeDisplay
                                            ].filter(Boolean).join(' - ')}
                                        </div>
                                    )}
                                    <div className={styles.itemMeta}>
                                        Qty: {item.quantity} × QAR {(item.wholesalePrice || 0).toFixed(2)}
                                    </div>
                                </div>
                                <div className={styles.itemTotal}>
                                    QAR {((item.wholesalePrice || 0) * item.quantity).toFixed(2)}
                                </div>
                            </div>
                        ))}
                    </div>
                    
                    <div className={styles.orderTotals}>
                        <div className={styles.totalRow}>
                            <span>Subtotal ({totalItems} items)</span>
                            <span>QAR {subtotal.toFixed(2)}</span>
                        </div>
                        <div className={styles.totalRow}>
                            <span>Shipping</span>
                            <span>QAR {shipping.toFixed(2)}</span>
                        </div>
                        <div className={styles.totalRowFinal}>
                            <span>Total</span>
                            <span>QAR {total.toFixed(2)}</span>
                        </div>
                    </div>
                </div>

                {/* Customer Info */}
                <div className={styles.customerInfo}>
                    <h3>Delivery Information</h3>
                    <div className={styles.customerDetails}>
                        <div className={styles.detailRow}>
                            <strong>Business:</strong> {wholesaleCustomer?.businessName}
                        </div>
                        <div className={styles.detailRow}>
                            <strong>Contact:</strong> {wholesaleCustomer?.contactName || 
                                `${wholesaleCustomer?.contactFirstName || ''} ${wholesaleCustomer?.contactLastName || ''}`.trim()}
                        </div>
                        <div className={styles.detailRow}>
                            <strong>Email:</strong> {wholesaleCustomer?.purchaseEmail || wholesaleCustomer?.email}
                        </div>
                        <div className={styles.detailRow}>
                            <strong>Phone:</strong> {wholesaleCustomer?.purchasePhone || wholesaleCustomer?.phone}
                        </div>
                        <div className={styles.detailRow}>
                            <strong>Address:</strong> {wholesaleCustomer?.streetAddress || wholesaleCustomer?.address || 'On file'}
                        </div>
                    </div>
                </div>

                {/* Payment Options */}
                <div className={styles.paymentSection}>
                    <h3>Payment Method</h3>
                    <div className={styles.paymentOptions}>
                        <button 
                            onClick={() => handlePlaceOrder('COD')}
                            disabled={isProcessing}
                            className={`${styles.paymentOption} ${styles.cod}`}
                        >
                            <FiTruck className={styles.paymentIcon} />
                            <div className={styles.paymentContent}>
                                <div className={styles.paymentTitle}>Cash on Delivery</div>
                                <div className={styles.paymentDesc}>Pay when your order arrives</div>
                            </div>
                        </button>

                        <button 
                            onClick={() => handlePlaceOrder('QLUB')}
                            disabled={isProcessing}
                            className={`${styles.paymentOption} ${styles.online}`}
                        >
                            <FiCreditCard className={styles.paymentIcon} />
                            <div className={styles.paymentContent}>
                                <div className={styles.paymentTitle}>Online Payment</div>
                                <div className={styles.paymentDesc}>Powered by Qlub - Cards accepted</div>
                            </div>
                        </button>

                        <button 
                            onClick={() => handlePlaceOrder('Bank Transfer')}
                            disabled={isProcessing}
                            className={`${styles.paymentOption} ${styles.transfer}`}
                        >
                            <FiDollarSign className={styles.paymentIcon} />
                            <div className={styles.paymentContent}>
                                <div className={styles.paymentTitle}>Bank Transfer</div>
                                <div className={styles.paymentDesc}>Direct transfer to our QNB account</div>
                            </div>
                        </button>
                    </div>

                    {/* Bank Details for Transfer */}
                    <div className={styles.bankDetails}>
                        <h4>Bank Transfer Details</h4>
                        <div className={styles.bankInfo}>
                            <p><strong>Bank:</strong> Qatar National Bank (QNB)</p>
                            <p><strong>Branch:</strong> Shemoukh Twin Towers, Doha</p>
                            <p><strong>Account Name:</strong> Grey Coffee</p>
                            <p><strong>IBAN:</strong> QA76QNBA000000000260072525001</p>
                            <p><strong>Swift:</strong> QNBAQAQ</p>
                        </div>
                        <p className={styles.bankNote}>
                            Please include your order number in the transfer reference.
                        </p>
                    </div>
                </div>

                {error && (
                    <div className={styles.errorMessage}>
                        {error}
                    </div>
                )}

                {isProcessing && (
                    <div className={styles.processingMessage}>
                        Processing your order...
                    </div>
                )}
            </div>
        </div>
    );
};

export default WholesaleCheckout;